import { useCallback, useEffect, useRef, useState } from 'react';

import { AutoComplete, AutoCompleteProps } from 'primereact/autocomplete';

import { SELECTION_LIMIT } from '@/constants';
import { fetchBreeds } from '@/services/lookups';
import { BreedResponse } from '@/services/lookups/types';

import './BreedsAutoComplete.css';

type BreedsAutoCompleteProps = Omit<AutoCompleteProps, 'defaultValue'> & {
  className?: string;
  label?: string;
  defaultValue?: BreedResponse;
  appendTo?: string | null;
  subFamilies?: number[];
};

export const BreedsAutoComplete = ({
  className,
  label,
  defaultValue,
  appendTo = 'self',
  onChange: externalOnChange,
  subFamilies,
  ...autoCompleteProps
}: BreedsAutoCompleteProps) => {
  const [selected, setSelected] = useState<BreedResponse | string | undefined>(
    defaultValue,
  );
  const [inputValue, setInputValue] = useState(defaultValue?.name || '');
  const [filtered, setFiltered] = useState<BreedResponse[]>([]);
  const userChangedRef = useRef(false);

  useEffect(() => {
    if (userChangedRef.current) {
      userChangedRef.current = false;
      return;
    }
    if (defaultValue) {
      setSelected(defaultValue);
      setInputValue(defaultValue.name || '');
    } else {
      setSelected(undefined);
      setInputValue('');
    }
  }, [defaultValue]);

  const search = useCallback(
    async (event: { query: string }) => {
      const results = await fetchBreeds({
        search: event.query,
        subFamilies,
      });
      setFiltered(results.slice(0, SELECTION_LIMIT));
    },
    [subFamilies],
  );

  const itemTemplate = (breed: BreedResponse) => {
    return `${breed.name} [${breed.code}]`;
  };

  const handleSelect = (e: { value: BreedResponse }) => {
    userChangedRef.current = true;
    setSelected(e.value);
    setInputValue(e.value?.name as string);
  };

  const handleChange = (e: { value: string | BreedResponse }) => {
    userChangedRef.current = true;
    const value =
      typeof e.value === 'object' && e.value !== null
        ? e.value.name
        : (e.value as string);
    setInputValue(value as string);
    externalOnChange?.(e as any);
  };

  return (
    <div
      className={`[ breeds-auto-complete ] [${className ? ` ${className}` : ''} label-input-group ]`}
    >
      {label && <label>{label}</label>}
      <AutoComplete
        appendTo={appendTo}
        suggestions={filtered}
        minLength={2}
        forceSelection
        selectionLimit={SELECTION_LIMIT}
        completeMethod={search}
        onSelect={handleSelect}
        onChange={handleChange}
        itemTemplate={itemTemplate}
        dropdown={true}
        {...autoCompleteProps}
        value={inputValue}
        className={className}
      />
    </div>
  );
};
